import IconData from "@/components/icon/Data.vue";
import IconFibre from "@/components/icon/Fibre.vue";
import IconNetwork from "@/components/icon/Network.vue";
import IconPhone from "@/components/icon/Phone.vue";
import IconCallTermination from "@/components/icon/CallTermination.vue";
import IconHelp from "@/components/icon/Help.vue";
import IconAward from "@/components/icon/Award.vue";
import IconAustralia from "@/components/icon/Australia.vue";

import type { Segment } from "@/types";

export type residentialImageCard =
  | "australianSupport"
  | "businessNbn"
  | "fastSetup"
  | "mobile"
  | "mostTrusted"
  | "proCustomerPromo"
  | "refer";

export type businessImageCard = "backup4g" | "businessNbn" | "aussieFibre";

export type enterpriseImageCard =
  | "aussieFibre"
  | "businessNbn"
  | "cloud"
  | "enterpriseEthernet"
  | "internet"
  | "network"
  | "security"
  | "voice"
  | "starlink";

export type enterpriseIconCard = string;

export type wholesaleImageCard =
  | "mspPartner"
  | "partnerProgram"
  | "whiteLabel"
  | "wholesaleCarrier";

export type wholesaleIconCard =
  | "data"
  | "aussieFibre"
  | "networkSolutions"
  | "voice"
  | "voiceCarrier"
  | "community"
  | "networkMap"
  | "trustedTelco"
  | "about";

export interface CardImageData {
  tag?: string;
  title: string;
  description?: string;
  img: {
    src: string;
    width: number;
    height: number;
    alt?: string;
    hideGradient?: boolean;
  };
  link?: {
    label: string;
    url: string;
    leftIcon?: Component | string;
    click?: () => void;
  };
  button?: {
    label: string;
    leftIcon?: Component;
  };
  badge?: {
    label: string;
    icon: string;
  };
  disclaimer?: string;
}

export interface CardIconData {
  title: string;
  description?: string;
  icon: Component | string;
  list?: string[];
  link?: {
    label: string;
    url?: string;
  };
  button?: {
    label: string;
  };
}

export interface CustomCardData {
  tag?: string;
  heading?: string;
  description?: string;
  disclaimer?: string;
  segment: Segment;
  columns?: 2 | 3 | 4;
  cards: CardIconData[];
}

const residentialImageCards: {
  [key in residentialImageCard]: CardImageData;
} = {
  businessNbn: {
    tag: "Business nbn® plans",
    title: "Business benefits at no extra cost",
    description:
      "Upgrade to business nbn® with your ABN to access dedicated business support and a <span class='uppercase'>free</span> static IP address at no extra cost.",
    img: {
      src: "/img/business/business-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "See business nbn® plans",
      url: "https://www.aussiebroadband.com.au/business/internet/nbn-plans/",
    },
  },
  refer: {
    tag: "Refer-a-friend",
    title: "Refer a friend and you'll each get $50 bonus credit",
    description:
      "Share your unique link with a mate or business needing some Aussie spirit and each receive a $50 bonus credit, how good is that! After your friend's service activates, you will both receive a credit. Credit is applied against monthly internet fees. <a href='https://assets.aussiebroadband.com.au/web/assets/legal/tcs/abb-general-promotions-tcs.pdf' target='_blank' class='text-link'>T&Cs apply</a>.",

    img: {
      src: "/img/residential/refer-a-friend-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "View refer-a-friend",
      url: "/refer-a-friend/",
    },
  },
  australianSupport: {
    title: "100% Australian-Based Support",
    description:
      "Our call centres are 100% Australian-based, with local experts standing by to help you with any question or issue you have - no matter how big or small.",
    img: {
      src: "/img/residential/senior-nbn-2-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
  },
  fastSetup: {
    title: "Fast and easy setup",
    description:
      "You don't have to be a computer genius - all you have to do is submit your order. We'll take care of everything else, from scheduling any required appointments, to helping set up your new modem.",
    img: {
      src: "/img/residential/senior-nbn-3-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
  },
  mobile: {
    tag: "Mobile plans",
    title: "Browse our awe-SIM mobile plans",
    description:
      "With reliable coverage and the speeds you need, we've got you covered with 4G, 5G, or data-only plans.",
    img: {
      src: "/img/residential/mobile-sim-plans-2-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "View mobile plans",
      url: "/mobile/sim-only-plans/",
    },
  },
  mostTrusted: {
    title: "Australia's most trusted telco",
    description:
      "We know you want a telco you can trust with your personal data. Well, we think we're pretty trustworthy, but don't just take it from us: we've been voted Roy Morgan's most trusted telco three years running.",
    img: {
      src: "/img/residential/senior-nbn-4-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
  },
  proCustomerPromo: {
    tag: "Introducing PRO!",
    title: "High upload speeds: now streaming on FTTP",
    description:
      "Our <a href='https://www.aussiebroadband.com.au/internet/ultrafast-nbn/' class='text-link'>new <span class='uppercase'>Pro</span> plans</a> feature professional level upload and download speeds, plus a free static IP. If you're after the best of the best and want top-tier upload speeds, then PRO is for you.",
    img: {
      src: "/img/residential/pro/introducing-pro-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Upgrade in MyAussie",
      url: "https://my.aussiebroadband.com.au/",
    },
  },
};

const businessImageCards: {
  [key in businessImageCard]: CardImageData;
} = {
  backup4g: {
    tag: "4G Backup",
    title: "Back your business nbn® with 4G backup",
    description:
      "Stay connected, no matter what, with reliable 4G backup. Included with high-speed business nbn® plans when purchased with eligible hardware.",
    img: {
      src: "/img/business/business-3-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Business 4G backup",
      url: "/business/mobile/4g-backup/",
    },
  },
  businessNbn: {
    tag: "Business nbn® plans",
    title: "Get business benefits at no extra cost",
    description:
      "Sign up with your ABN to access dedicated business support and a free static IP address at no extra cost.",
    img: {
      src: "/img/business/business-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Business nbn® plans",
      url: "https://www.aussiebroadband.com.au/business/internet/nbn-plans/",
    },
  },
  aussieFibre: {
    tag: "Aussie fibre",
    title: "Future-proof your business with ultrafast Aussie Fibre",
    description:
      "Access up to 10,000Mbps internet plans on the Aussie Fibre network with $0 installation on a 36 month contract for eligible sites.",
    img: {
      src: "/img/business/aussie-fibre-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Aussie fibre internet",
      url: "/business/internet/aussie-fibre/",
    },
  },
};

const enterpriseImageCards: {
  [key in enterpriseImageCard | wholesaleImageCard]: CardImageData;
} = {
  // Enterprise
  aussieFibre: {
    tag: "Aussie Fibre",
    title: "Ultrafast Aussie Fibre Internet to power your growing enterprise.",
    description:
      "High-speed connections delivering up to 10,000/10,000Mbps on our own fibre network.",
    img: {
      src: "/img/business/aussie-fibre-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Find out more",
      url: "/business/internet/aussie-fibre/",
    },
  },
  businessNbn: {
    tag: "Business nbn®",
    title: "High-speed nbn® connectivity to power your business.",
    description:
      "All the bandwidth you need to run high-consumption apps and software.",
    img: {
      src: "/img/business/business-nbn-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Find out more",
      url: "/enterprise/internet/nbn-plans/",
    },
  },
  cloud: {
    tag: "Managed Cloud",
    title: "Cloud technology hosted in Australian data centres.",
    description:
      "Increase security, control, and scalability in your IT environment with private cloud, data protection, and storage solutions.",
    img: {
      src: "/img/enterprise/cloud-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "See cloud solutions",
      url: "/enterprise/cloud/",
    },
  },
  enterpriseEthernet: {
    tag: "Enterprise Ethernet",
    title: "Upgrade to fast fibre connectivity with nbn® Enterprise Ethernet.",
    description: "Supercharged connectivity with plans up to 1000/1000Mbps.",
    img: {
      src: "/img/business/enterprise-ethernet-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Find out more",
      url: "/enterprise/internet/enterprise-ethernet/",
    },
  },
  internet: {
    tag: "Internet",
    title: "Experience speed and reliability the Aussie way.",
    description:
      "Seamlessly handle high bandwidth applications and data transfer with connectivity options of up to 10,000/10,000Mbps.",
    img: {
      src: "/img/enterprise/internet-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "See internet solutions",
      url: "/enterprise/internet/",
    },
  },
  network: {
    tag: "Managed Networks",
    title: "Support critical functions with high-performance networking.",
    description:
      "Boost the performance and efficiency of your network traffic with SD-WAN, Private IP, data centre connectivity, and hosted firewall.",
    img: {
      src: "/img/enterprise/network-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "See network solutions",
      url: "/enterprise/network/",
    },
  },
  security: {
    tag: "Managed Security",
    title: "Equip your workplace with enterprise-grade security solutions.",
    description:
      "Protect your data and insulate your environment from a wide range of cyber risks with robust email, endpoint, and firewall solutions.",
    img: {
      src: "/img/enterprise/security-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "See security solutions",
      url: "/enterprise/security/",
    },
  },
  voice: {
    tag: "Mobile and voice",
    title: "Boost the effectiveness of your voice interactions.",
    description:
      "Facilitate better communication and collaboration with voice solutions including Hosted PBX, mobile SIMs, SIP Trunks and more.",
    img: {
      src: "/img/enterprise/mobile-and-voice-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Mobile and voice solutions",
      url: "/enterprise/mobile-voice/",
    },
  },
  starlink: {
    tag: "Starlink",
    title:
      "Seamless high-speed internet delivered from cutting-edge satellite tech.",
    description:
      "Tap into Starlink's reliable, high-speed satellite connectivity.",
    img: {
      src: "/img/enterprise/starlink-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Find out more",
      url: "/enterprise/starlink/",
    },
  },
  // Wholesale
  mspPartner: {
    tag: "",
    title: "MSP Partner",
    description:
      "Become a one-stop IT and telco shop for your business customers.",
    img: {
      src: "/img/wholesale/msp-partner-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Find out more",
      url: "/wholesale/msp/",
    },
  },
  partnerProgram: {
    tag: "",
    title: "Partner Program",
    description:
      "Grow your business by introducing your customers to our nbn® plans.",
    img: {
      src: "/img/wholesale/parter-program-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Find out more",
      url: "/wholesale/partner-program/",
    },
  },
  whiteLabel: {
    tag: "",
    title: "White Label",
    description:
      "Offer our award-winning plans under your brand and improve customer loyalty.",
    img: {
      src: "/img/wholesale/white-label-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Find out more",
      url: "/wholesale/white-label/",
    },
  },
  wholesaleCarrier: {
    tag: "",
    title: "Wholesale Carrier",
    description:
      "Expand your network, data and voice capacity with our scalable services.",
    img: {
      src: "/img/wholesale/wholesale-telco-1-720x480.webp",
      width: 720,
      height: 480,
      alt: "",
    },
    link: {
      label: "Find out more",
      url: "/wholesale/carrier/",
    },
  },
};

const enterpriseIconCards: {
  [key in enterpriseIconCard | wholesaleIconCard]: CardIconData;
} = {
  // Wholesale
  data: {
    title: "Data Services",
    description:
      "Easy to configure and manage for you, fast and reliable for your customers.",
    list: ["nbn® TC-4", "Opticomm", "nbn® Enterprise Ethernet"],
    icon: IconData,
    link: {
      label: "Find out more",
      url: "/wholesale/services/data/",
    },
  },
  aussieFibre: {
    title: "Aussie Fibre",
    description:
      "Dedicated on-net Fibre Ethernet delivered over the Aussie network.",
    list: [
      "Plans up to 10Gbps",
      "Symmetrical bandwidth",
      "4 hour resolution time",
    ],
    icon: IconFibre,
    link: {
      label: "Find out more",
      url: "/wholesale/services/aussie-fibre/",
    },
  },
  networkSolutions: {
    title: "Network Solutions",
    description:
      "Solutions to grow network capacity where and when it is needed.",
    list: ["Dark Fibre", "Ethernet", "IP Transit"],
    icon: IconNetwork,
    link: {
      label: "Find out more",
      url: "/wholesale/services/network-solutions/",
    },
  },
  voice: {
    title: "Voice Systems",
    description:
      "Hosted PBX, SIP and VoIP solutions to enable your customers in any environment.",
    list: ["Hosted PBX", "SIP"],
    icon: IconPhone,
    link: {
      label: "Find out more",
      url: "/wholesale/services/voice/",
    },
  },
  voiceCarrier: {
    title: "Voice Carrier",
    description:
      "Fully interconnected voice carriage connecting Australia and overseas.",
    list: [
      "Call Termination Services",
      "Fixed, Mobile and Toll-free Phone Numbers",
      "Phone Number Ranges",
    ],
    icon: IconCallTermination,
    link: {
      label: "Find out more",
      url: "/wholesale/services/voice-carrier/",
    },
  },
  community: {
    title: "Building Better Communities",
    description:
      "Find out more about our efforts to give back through our certified B Corp activities.",
    icon: IconHelp,
    link: {
      label: "Find out more",
      url: "/about-us/community/",
    },
  },
  networkMap: {
    title: "Our Growing Network",
    description:
      "More fibre, more experts, more presence, Aussie Broadband is growing.",
    icon: IconNetwork,
    link: {
      label: "Find out more",
      url: "/network/map/",
    },
  },
  trustedTelco: {
    title: "Australia's Most Trusted Telco",
    description:
      "Awarded two years in a row by Roy Morgan, we're committed to great experiences.",
    icon: IconAward,
    link: {
      label: "Find out more",
      url: "https://www.aussiebroadband.com.au/media-centre/aussie-broadband-wins-3-roy-morgan-customer-satisfaction-awards/",
    },
  },
  about: {
    title: "About Us",
    description:
      "We haven't forgotten our roots since starting in regional Victoria. See how we're changing the telco game.",
    icon: IconAustralia,
    link: {
      label: "Find out more",
      url: "/about-us/",
    },
  },
};

export const getResidentialImageCards = (cards: residentialImageCard[]) => {
  const selectedCards: CardImageData[] = [];
  cards.forEach((card) => selectedCards.push(residentialImageCards[card]));
  return selectedCards;
};

export const getBusinessImageCards = (cards: businessImageCard[]) => {
  const selectedCards: CardImageData[] = [];
  cards.forEach((card) => selectedCards.push(businessImageCards[card]));
  return selectedCards;
};

export const getEnterpriseImageCards = (
  cards: (enterpriseImageCard | wholesaleImageCard)[],
) => {
  const selectedCards: CardImageData[] = [];
  cards.forEach((card) => selectedCards.push(enterpriseImageCards[card]));
  return selectedCards;
};

export const getEnterpriseIconCards = (
  cards: (enterpriseIconCard | wholesaleIconCard)[],
) => {
  const selectedCards: CardIconData[] = [];
  cards.forEach((card) => selectedCards.push(enterpriseIconCards[card]));
  return selectedCards;
};
